'use client'

import { useStoreI18n, useStoreTour } from '@/store'
import {
  Typo3FacetAttributesContent,
  Typo3SectionSpotSanityAccomodation,
  Typo3SectionSpotSanityCulture,
  Typo3SectionSpotSanityEatDrink,
  Typo3SectionSpotSanityEventList,
  Typo3SectionSpotSanityFreetime,
  Typo3SectionSpotSanityNature,
  Typo3SectionSpotSanityPressroom,
  Typo3SectionSpotSanityService,
  Typo3SectionSpotSanityShopping,
  Typo3SectionSpotSanitySport,
  Typo3SectionSpotSanitySportServices,
  Typo3SectionSpotSanityStory,
  Typo3SectionSpotSanityTour
} from '@/types'
import {
  Checkbox,
  SpotSanityCheckbox,
  SpotSanityDates,
  Switch
} from '@/ui/components'
import FilterSlider from '@/ui/components/FilterSlider/FilterSlider'
import { getDifficultyOptions } from '@/utils'
import { getPlugin } from '@/utils/section/get-plugin'
import { memo, useMemo } from 'react'
import { useDebounceCallback } from 'usehooks-ts'
// import { useDebounceCallback } from 'usehooks-ts'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  data:
    | Typo3SectionSpotSanityAccomodation
    | Typo3SectionSpotSanityCulture
    | Typo3SectionSpotSanityService
    | Typo3SectionSpotSanityEatDrink
    | Typo3SectionSpotSanityFreetime
    | Typo3SectionSpotSanityNature
    | Typo3SectionSpotSanityShopping
    | Typo3SectionSpotSanitySport
    | Typo3SectionSpotSanitySportServices
    | Typo3SectionSpotSanityEventList
    | Typo3SectionSpotSanityTour
    | Typo3SectionSpotSanityStory
    | Typo3SectionSpotSanityPressroom
  store: any
}

const FilterTitle = ({ children }: { children: React.ReactNode }) => (
  <p className="textstyle-md-bold">{children}</p>
)

const FiltersSection = ({
  children,
  props
}: {
  children: React.ReactNode
  props?: React.HTMLAttributes<HTMLDivElement>
}) => (
  <div
    className="gap-y-md py-lg grid auto-rows-auto grid-cols-1 border-t border-t-neutral-300"
    {...props}
  >
    {children}
  </div>
)

const SpotSanityFiltersModal = ({ data, store, ...props }: Props) => {
  const plugin = getPlugin(data)
  const facets = data?.content?.facets
  const { isOpenToday, setIsOpenToday, isTopEvents, setIsTopEvents } = store()
  //Only for tours
  const {
    distance,
    setDistance,
    duration,
    setDuration,
    difficulty,
    setDifficulty
  } = useStoreTour()

  // Difficulty options are derived from the tour items (not a TYPO3 facet)
  const difficultyOptions = useMemo(
    () =>
      data.type === 'spotsanity_tour'
        ? getDifficultyOptions(
            (data as Typo3SectionSpotSanityTour).content?.items
          )
        : [],
    [data]
  )

  //hooks
  const debouncedDistance = useDebounceCallback(setDistance, 100)
  const debouncedDuration = useDebounceCallback(setDuration, 100)

  const distanceValue = Array.isArray(distance.selected)
    ? distance.selected.map((item: any) =>
        item >= 60000 ? 60 : Math.round(item / 1000)
      )
    : [
        Math.round(distance.min / 1000),
        Math.min(Math.round(distance.max / 1000), 60)
      ]
  const durationValue = Array.isArray(duration.selected)
    ? duration.selected.map((item: number) =>
        item >= duration.max ? 8 : Math.round(item / 60)
      )
    : [
        Math.round(duration.min / 60),
        Math.min(Math.round(duration.max / 60), 8)
      ]

  const handleSwitch = () => {
    setIsOpenToday(!isOpenToday)
  }

  const handleSwitchTopEvents = () => {
    setIsTopEvents(!isTopEvents)
  }

  const handleDifficultyToggle = (value: string) => {
    if (difficulty.includes(value)) {
      setDifficulty(difficulty.filter((item: string) => item !== value))
    } else {
      setDifficulty([...difficulty, value])
    }
  }

  const getVariant = ():
    | 'hotel'
    | 'event'
    | 'tour'
    | 'story'
    | 'nature'
    | 'sanityPlugin' => {
    if (data.type === 'spotsanity_accommodation') {
      return 'hotel'
    } else if (data.type === 'spotsanity_eventlist') {
      return 'event'
    } else if (data.type === 'spotsanity_tour') {
      return 'tour'
    } else if (data.type === 'spotsanity_story') {
      return 'story'
    } else if (data.type === 'spotsanity_nature') {
      return 'nature'
    } else {
      return 'sanityPlugin'
    }
  }

  //stores
  const D = useStoreI18n.use.dictionary()

  // console.log(facets?.events?.facetAttributes)

  return (
    <div className="flex flex-col" {...props}>
      {getVariant() !== 'story' &&
        getVariant() !== 'nature' &&
        getVariant() !== 'event' &&
        plugin &&
        plugin.hasShowOpenClosed === true && (
          <FiltersSection>
            <FilterTitle>{D['fe.plugin.filters.opening']}</FilterTitle>
            <Switch
              className="justify-between"
              id="openToday"
              label={D['fe.plugin.openToday']}
              labelClassName="textstyle-md-regular"
              switchProps={{
                checked: isOpenToday,
                onClick: handleSwitch
              }}
            />
          </FiltersSection>
        )}
      {getVariant() === 'tour' && (
        <>
          {difficultyOptions.length > 1 && (
            <FiltersSection>
              <FilterTitle>{D['be.tour.filter.difficulty']}</FilterTitle>
              <div className="gap-y-md grid grid-cols-1 gap-x-0 capitalize sm:grid-cols-3">
                {difficultyOptions.map((option) => (
                  <Checkbox
                    key={option.value}
                    id={`difficulty-${option.value}`}
                    label={option.label}
                    checked={difficulty.includes(option.value)}
                    onChange={() => handleDifficultyToggle(option.value)}
                  />
                ))}
              </div>
            </FiltersSection>
          )}
          <FiltersSection>
            <FilterTitle>{D['be.tour.filter.distance']}</FilterTitle>
            <FilterSlider
              aria-label="Distance"
              showOutput={false}
              value={distanceValue}
              max={60} // Set max value to 60
              thumbLabels={['start', 'end']}
              onValueChange={(value) => {
                if (Array.isArray(value)) {
                  // When slider reaches 60, we treat it as the maximum distance (e.g., 60000 meters or greater)
                  const scaledValue = value.map((v: any) =>
                    v === 60 ? distance.max : v * 1000
                  )
                  debouncedDistance({
                    ...distance,
                    selected: scaledValue as number[]
                  })
                }
              }}
            />

            <div className="text-center">
              {Array.isArray(distance.selected)
                ? distance.selected
                    .map((item: any) => {
                      const kmValue = Math.round(item / 1000)
                      // Display '>60 km' if the value is 60000 meters or more, else show the rounded value
                      return kmValue >= 60 ? '>60 km' : `${kmValue} km`
                    })
                    .join(' - ')
                : `${Math.round(distance.min / 1000)} km - ${
                    Math.round(distance.max / 1000) > 60
                      ? '>60 km'
                      : `${Math.round(distance.max / 1000)} km`
                  }`}
            </div>
          </FiltersSection>
          <FiltersSection>
            <FilterTitle>{D['be.tour.filter.duration']}</FilterTitle>

            <FilterSlider
              aria-label="Duration"
              data-slot="slider"
              value={durationValue}
              max={8}
              onValueChange={(value) => {
                if (Array.isArray(value)) {
                  // If the slider reaches 8, map it to the maximum duration (duration.max)
                  const scaledValue = value.map((v: number) =>
                    v === 8 ? duration.max : v * 60
                  )
                  debouncedDuration({
                    ...duration,
                    selected: scaledValue as number[]
                  })
                }
              }}
            />

            <div className="text-center">
              {Array.isArray(duration.selected)
                ? duration.selected
                    .map((item: number) => {
                      const hours = Math.round(item / 60)
                      // Display '>8 h' if the value represents the max duration
                      return hours >= 8 ? '>8 h' : `${hours} h`
                    })
                    .join(' - ')
                : `${Math.round(duration.min / 60)} h - ${
                    Math.round(duration.max / 60) > 8
                      ? '>8 h'
                      : `${Math.round(duration.max / 60)} h`
                  }`}
            </div>
          </FiltersSection>
        </>
      )}
      {getVariant() === 'event' &&
        data?.plugin &&
        'hasShowFilterTopEvents' in data.plugin &&
        data.plugin.hasShowFilterTopEvents && (
          <FiltersSection>
            <FilterTitle>{D['fe.plugin.filters.opening']}</FilterTitle>
            <Switch
              className="justify-between"
              id="topEvents"
              labelClassName="textstyle-md-regular"
              label={D['fe.events.filters.top']}
              switchProps={{
                checked: isTopEvents,
                onClick: handleSwitchTopEvents
              }}
            />
          </FiltersSection>
        )}
      {((getVariant() !== 'hotel' &&
        data.plugin &&
        'hasShowFormOnline' in data.plugin &&
        data.plugin.hasShowFormOnline) ||
        getVariant() === 'event') && (
        <FiltersSection>
          <FilterTitle>{D['fe.events.filters.dates']}</FilterTitle>
          <div className="gap-y-md gap-xs flex flex-col justify-between md:grid md:grid-cols-2">
            <SpotSanityDates store={store} isInModal />
          </div>
        </FiltersSection>
      )}
      {facets &&
        Object.keys(facets)
          .filter(
            (key: string) =>
              Object.values(facets[key]?.facetAttributes).filter(
                (facet: Typo3FacetAttributesContent) => facet.isActive
              ).length > 0
          )
          .map((key: string, index: number) => (
            <FiltersSection key={index?.toString()}>
              <FilterTitle>{facets[key]?.name}</FilterTitle>
              <div className="gap-y-md grid grid-cols-1 gap-x-0 sm:grid-cols-2">
                {facets &&
                  Object.values(facets[key]?.facetAttributes).map(
                    (facet: Typo3FacetAttributesContent) => {
                      if (facet.isActive) {
                        return (
                          <SpotSanityCheckbox
                            store={store}
                            key={facet.facedId}
                            data={facet}
                            id={facet.facedId}
                            isDropdown={
                              key === 'resort-location' ? true : false
                            }
                          >
                            {facet.title}
                          </SpotSanityCheckbox>
                        )
                      }
                    }
                  )}
              </div>
            </FiltersSection>
          ))}
    </div>
  )
}

export default memo(SpotSanityFiltersModal)
